import { NumberFormatHelper } from "app/core/helpers/number-format.helper";

export interface VatDeclarationResponse {
  id?: number;

  has_steuernummer?: boolean;
  steuernummer?: number;
  tax_authority?: number;
  declaration_type?: string;
  last_year_income?: number;
  income_sum?: number;
  type_of_taxation?: string;
  general_rate_net_revenues?: number;
  reduced_rate_net_revenues?: number;
  zero_percent_net_revenues?: number;
  reverse_charge?: boolean;
  reverse_charge_first?: number;
  reverse_charge_first_vat_rate?: string;
  reverse_charge_second?: number;
  reverse_charge_second_vat_rate?: string;
  reverse_charge_third?: number;
  reverse_charge_third_vat_rate?: string;
  sales_value_settled_by_recipient?: number;
  vat_refund_value?: string;
  advance_payments?: string;
  premium_selected?: boolean;
}

export interface VatDeclarationErrors {
  has_steuernummer?: boolean;
  steuernummer?: number;
  tax_authority?: number;
  declaration_type?: string;
  last_year_income?: string;
  income_sum?: string;
  type_of_taxation?: string;
  general_rate_net_revenues?: number;
  reduced_rate_net_revenues?: number;
  zero_percent_net_revenues?: number;
  reverse_charge?: boolean;
  reverse_charge_first?: number;
  reverse_charge_first_vat_rate?: string;
  reverse_charge_second?: number;
  reverse_charge_second_vat_rate?: string;
  reverse_charge_third?: number;
  reverse_charge_third_vat_rate?: string;
  sales_value_settled_by_recipient?: number;
  vat_refund_value?: string;
  advance_payments?: string;
  premium_selected?: boolean;
}

export interface VatDeclarationInterface {
  id: number;

  has_steuernummer?: boolean;
  steuernummer?: number;
  tax_authority?: number;
  declaration_type?: string;
  last_year_income?: number;
  income_sum?: number;
  type_of_taxation?: string;
  general_rate_net_revenues?: number;
  reduced_rate_net_revenues?: number;
  zero_percent_net_revenues?: number;
  reverse_charge?: boolean;
  reverse_charge_first?: number;
  reverse_charge_first_vat_rate?: string;
  reverse_charge_second?: number;
  reverse_charge_second_vat_rate?: string;
  reverse_charge_third?: number;
  reverse_charge_third_vat_rate?: string;
  sales_value_settled_by_recipient?: number;
  vat_refund_value?: number;
  advance_payments?: number;
  premium_selected?: boolean;
}

export class VatDeclaration {
  id: number;

  has_steuernummer?: boolean = false;
  steuernummer?: number = null;
  tax_authority?: number = null;
  declaration_type?: string = null;
  last_year_income? = null;
  income_sum? = null;
  type_of_taxation?: string = null;
  general_rate_net_revenues?: number = null;
  reduced_rate_net_revenues?: number = null;
  zero_percent_net_revenues?: number = null;
  reverse_charge?: boolean = false;
  reverse_charge_first?: number = null;
  reverse_charge_first_vat_rate?: string = null;
  reverse_charge_second?: number = null;
  reverse_charge_second_vat_rate?: string = null;
  reverse_charge_third?: number = null;
  reverse_charge_third_vat_rate?: string = null;
  sales_value_settled_by_recipient?: number = null;
  vat_refund_value?: string;
  advance_payments?: string = "0";
  premium_selected?: boolean;

  public static fromResponse(response: VatDeclarationResponse): VatDeclaration {
    const vatDeclaration = new VatDeclaration();
    vatDeclaration.id = response.id;

    vatDeclaration.has_steuernummer = response.has_steuernummer;
    vatDeclaration.steuernummer = response.steuernummer;
    vatDeclaration.tax_authority = response.tax_authority;
    vatDeclaration.declaration_type = response.declaration_type;
    vatDeclaration.last_year_income = Number(response.last_year_income) || 0.0;
    vatDeclaration.income_sum = Number(response.income_sum) || 0.0;
    vatDeclaration.type_of_taxation = response.type_of_taxation;
    vatDeclaration.general_rate_net_revenues = response.general_rate_net_revenues;
    vatDeclaration.reduced_rate_net_revenues = response.reduced_rate_net_revenues;
    vatDeclaration.zero_percent_net_revenues = response.zero_percent_net_revenues;
    vatDeclaration.reverse_charge = response.reverse_charge;
    vatDeclaration.reverse_charge_first = response.reverse_charge_first;
    vatDeclaration.reverse_charge_first_vat_rate = response.reverse_charge_first_vat_rate;
    vatDeclaration.reverse_charge_second = response.reverse_charge_second;
    vatDeclaration.reverse_charge_second_vat_rate = response.reverse_charge_second_vat_rate;
    vatDeclaration.reverse_charge_third = response.reverse_charge_third;
    vatDeclaration.reverse_charge_third_vat_rate = response.reverse_charge_third_vat_rate;
    vatDeclaration.sales_value_settled_by_recipient = response.sales_value_settled_by_recipient;
    vatDeclaration.vat_refund_value = response.vat_refund_value;
    vatDeclaration.advance_payments = response.advance_payments;
    vatDeclaration.premium_selected = response.premium_selected;

    return vatDeclaration;
  }

  public static fromStorage(response: VatDeclaration): VatDeclaration {
    const vatDeclaration = new VatDeclaration();
    vatDeclaration.id = response.id;

    vatDeclaration.has_steuernummer = response.has_steuernummer;
    vatDeclaration.steuernummer = response.steuernummer;
    vatDeclaration.tax_authority = response.tax_authority;
    vatDeclaration.declaration_type = response.declaration_type;
    vatDeclaration.last_year_income = Number(response.last_year_income) || 0.0;
    vatDeclaration.income_sum = Number(response.income_sum) || 0.0;
    vatDeclaration.type_of_taxation = response.type_of_taxation;
    vatDeclaration.general_rate_net_revenues = response.general_rate_net_revenues;
    vatDeclaration.reduced_rate_net_revenues = response.reduced_rate_net_revenues;
    vatDeclaration.zero_percent_net_revenues = response.zero_percent_net_revenues;
    vatDeclaration.reverse_charge = response.reverse_charge;
    vatDeclaration.reverse_charge_first = response.reverse_charge_first;
    vatDeclaration.reverse_charge_first_vat_rate = response.reverse_charge_first_vat_rate;
    vatDeclaration.reverse_charge_second = response.reverse_charge_second;
    vatDeclaration.reverse_charge_second_vat_rate = response.reverse_charge_second_vat_rate;
    vatDeclaration.reverse_charge_third = response.reverse_charge_third;
    vatDeclaration.reverse_charge_third_vat_rate = response.reverse_charge_third_vat_rate;
    vatDeclaration.sales_value_settled_by_recipient = response.sales_value_settled_by_recipient;
    vatDeclaration.vat_refund_value = response.vat_refund_value;
    vatDeclaration.advance_payments = response.advance_payments;
    vatDeclaration.premium_selected = response.premium_selected;

    return vatDeclaration;
  }

  constructor() {}

  // public get incomeNet(): string {
  //   return NumberFormatHelper.toFixed2(this.income - this.costs);
  // }

  public payload(defaultValue = undefined): VatDeclarationResponse {
    return {
      id: this.id || undefined,

      has_steuernummer: this.has_steuernummer,
      steuernummer: this.steuernummer,
      tax_authority: this.tax_authority,
      declaration_type: this.declaration_type,

      type_of_taxation: this.type_of_taxation,
      general_rate_net_revenues: this.general_rate_net_revenues,
      reduced_rate_net_revenues: this.reduced_rate_net_revenues,
      zero_percent_net_revenues: this.zero_percent_net_revenues,
      reverse_charge: this.reverse_charge,
      reverse_charge_first: this.reverse_charge_first,
      reverse_charge_first_vat_rate: this.reverse_charge_first_vat_rate,
      reverse_charge_second: this.reverse_charge_second,
      reverse_charge_second_vat_rate: this.reverse_charge_second_vat_rate,
      reverse_charge_third: this.reverse_charge_third,
      reverse_charge_third_vat_rate: this.reverse_charge_third_vat_rate,
      sales_value_settled_by_recipient: this.sales_value_settled_by_recipient,
      vat_refund_value: this.vat_refund_value,
      advance_payments: this.advance_payments,
      premium_selected: this.premium_selected
    };
  }
}
